import React, { useState, useMemo, useEffect } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Box,
  Typography,
  Button,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
} from '@mui/material';

import { ReactComponent as MarektingCardIcon } from "../../assets/images/icons/usage_and_charges/marketing_card.svg";
import { ReactComponent as TotalAmountCardIcon } from "../../assets/images/icons/usage_and_charges/payment_amount_card.svg";
import { ReactComponent as UtilityCardIcon } from "../../assets/images/icons/usage_and_charges/utility_card.svg";
import { ReactComponent as ServicesCardIcon } from "../../assets/images/icons/usage_and_charges/services_card.svg";
import { FileDownload as ExportIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';

import { DAILY_USAGE_ANALYTICS_ENDPOINT } from '../../config/urls';
import { useAuth } from '../../hooks/AuthProvider';
import dayjs from "dayjs";
import { borderColors, ColorsConstants, grayFontColor } from '../../cssUtils/constants';
import CustomTabs from '../../common/custom-tabs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import AppTable from '../../common/app-table';
import typography from '../../cssUtils/typography';
import RCSUsageDashboard from './RcsFetchUsage';
import { formatDateOnly, formatWithTzLabel } from '../../utils/dateUtils';

const UsageCharges = ({ dateInput }) => {
  // 🟢 1. Fetch permissions to determine channel access FIRST
  const permissionsFromStorage = JSON.parse(localStorage.getItem("permissions") || "[]");
  const hasRcsBotCreate = permissionsFromStorage.includes("rcs_bot_create.view");
  // conversations.view is granted only by WhatsApp addons, so it marks the WA channel
  const hasWa = permissionsFromStorage.includes("conversations.view");

  // 🟢 2. SMART DEFAULT: Default to RCS (1) if RCS user, otherwise WhatsApp (0)
  const initialTab = hasWa ? 0 : 1;
  
  const [currentChannel, setCurrentChannel] = useState('whatsapp');
  const [currentTab, setCurrentTab] = useState(0);
  const [activeTab, setActiveTab] = useState(initialTab); // <-- Used smart default here

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const [monthlyUsage, setMonthlyUsage]           = useState([]);
  const [rcsUsageData, setRcsUsageData]           = useState([]); 
  const [chargesPerCountry, setChargesPerCountry] = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [lastSync, setLastSync]                   = useState(null);
  const [currencyCode, setCurrencyCode]           = useState("INR");


  // ── Pagination — country table ──────────────────────────────────────────────
  const [controller, setController]   = useState({ page: 0, rowsPerPage: 20 });
  const [rowCount, setRowCount]       = useState(0);

  const handleChangePage = (event, newPage) =>
    setController((prev) => ({ ...prev, page: newPage }));

  const handleChangeRowsPerPage = (event) =>
    setController({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });

  // ── Pagination — monthly usage table ───────────────────────────────────────
  const [monthlyController, setMonthlyController] = useState({ page: 0, rowsPerPage: 20 });

  const handleMonthlyPageChange = (event, newPage) =>
    setMonthlyController((prev) => ({ ...prev, page: newPage }));

  const handleMonthlyRowsPerPageChange = (event) =>
    setMonthlyController({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });

  const { getValidToken } = useAuth();

  // ── Helpers ────────────────────────────────────────────────────────────────

  const getCurrencySymbol = (code) => {
    const map = {
      INR: "₹", SAR: "﷼", AED: "د.إ", ZAR: "R", MYR: "RM", IDR: "Rp",
      PEN: "S/", CLP: "$", COP: "$", BRL: "R$", MXN: "$", KES: "KSh",
      ZMW: "ZK", ETB: "Br", CDF: "FC", GNF: "FG", SGD: "S$", PHP: "₱",
      USD: "$", EUR: "€", GBP: "£",
    };
    return map[code?.toUpperCase()] ?? code ?? "";
  };

  const currencySymbol = getCurrencySymbol(currencyCode);

  const formatMonthYear = (value) =>
    value ? dayjs(value + "-01").format("MMMM YYYY") : "";

  const formatDateTime = (isoString) =>
    isoString ? formatWithTzLabel(isoString, "DD MMMM YYYY, hh:mm A") : "";

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchUsageAnalytics = async () => {
    try {
      const token  = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const headers = {
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
        Authorization: `Bearer ${token}`,
      };

      const response = await fetch(
        `${DAILY_USAGE_ANALYTICS_ENDPOINT}?month=${selectedMonth}`,
        { headers }
      );
      if (!response.ok) throw new Error("Failed to fetch usage analytics");

      const jsonData = await response.json();

      const charges = Array.isArray(jsonData?.charges) ? jsonData.charges : [];
      setMonthlyUsage(Array.isArray(jsonData?.usage) ? jsonData.usage : []);
      setChargesPerCountry(charges);
      setRowCount(charges.length);
      setLastSync(formatDateTime(jsonData?.last_sync));
      setCurrencyCode(jsonData?.currency || "INR");
    } catch (error) {
      console.error("Error fetching usage analytics:", error.message);
    }
  };

  useEffect(() => {
    fetchUsageAnalytics();
    setController({ page: 0, rowsPerPage: 20 });
    setMonthlyController({ page: 0, rowsPerPage: 20 });
  }, [selectedMonth]);

  // ── Stats ──────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    if (!monthlyUsage || monthlyUsage.length === 0) {
      return {
        total:     { messages: 0, charges: 0 },
        marketing: { messages: 0, charges: 0 },
        utility:   { messages: 0, charges: 0 },
        service:   { messages: 0, charges: 0 },
      };
    }
    const sum = (field) => monthlyUsage.reduce((acc, r) => acc + r[field], 0);
    return {
      total:     { messages: sum("marketing") + sum("utility") + sum("services"), charges: sum("total") },
      marketing: { messages: sum("marketing"), charges: sum("marketingCharges") },
      utility:   { messages: sum("utility"),   charges: sum("utilityCharges")   },
      service:   { messages: sum("services"),  charges: sum("serviceCharges")   },
    };
  }, [monthlyUsage]);

  const topCountries = useMemo(() => {
    if (!Array.isArray(chargesPerCountry)) return [];
    return chargesPerCountry
      .map((row) => {
        const marketing = row.marketing?.count ?? 0;
        const utility   = row.utility?.count   ?? 0;
        const service   = row.service?.count   ?? 0;
        const auth      = row.auth?.count      ?? 0;
        return { country: row.country, marketing, utility, service, auth, total: marketing + utility + service + auth };
      })
      .sort((a, b) => b.total - a.total);
  }, [chargesPerCountry]);

  // ── Export ─────────────────────────────────────────────────────────────────

  const handleExport = () => {
    if (!Array.isArray(monthlyUsage) || monthlyUsage.length === 0) {
      alert("No data available to export");
      return;
    }
    const headers = ["Date","Currency","Marketing Messages","Utility Messages","Service Messages","Marketing Charges","Utility Charges","Service Charges","Total Charges"];
    const rows = monthlyUsage.map((row) => [
      row.date_iso, row.currency, row.marketing, row.utility, row.services,
      row.marketingCharges, row.utilityCharges, row.serviceCharges, row.total,
    ]);
    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `monthly_usage_${selectedMonth}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 🟢 NEW: Dedicated RCS Export Function
  const handleRcsExport = () => {
    if (!Array.isArray(rcsUsageData) || rcsUsageData.length === 0) {
      alert("No RCS data available to export");
      return;
    }
    
    // Canonical billing groups, same keys/labels as the RCS usage table
    const groups = [["basic", "BASIC"], ["single", "SINGLE"], ["p2a", "P2A"], ["conversation", "CONVERSATION"]];
    const headers = [
      "Date", "Currency",
      ...groups.map(([, label]) => `${label} Messages`), "Total Messages",
      ...groups.map(([, label]) => `${label} Charges`), "Total Charges",
    ];

    const rows = rcsUsageData.map((row) => [
      row.date,
      row.currency || "",
      ...groups.map(([key]) => row[`${key}Messages`] || 0),
      row.totalMessages || 0,
      ...groups.map(([key]) => Number(row[`${key}Charges`] || 0).toFixed(4)),
      Number(row.totalCharges || 0).toFixed(4),
    ]);

    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `rcs_monthly_usage_${selectedMonth}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Column definitions ─────────────────────────────────────────────────────

  const tableColumns = [
    { id: "date",     label: "Date",     render: (row) => formatDateOnly(row.date_iso) },
    { id: "currency", label: "Currency", render: () => currencyCode },
    { id: "marketing", label: "Marketing Messages" },
    { id: "utility",   label: "Utility Messages"   },
    { id: "services",  label: "Services"            },
    { id: "authentication",  label: "Authentication Messages"},
    { id: "marketingCharges", label: "Marketing Charges", render: (row) => `${currencySymbol}${row.marketingCharges.toFixed(4)}` },
    { id: "utilityCharges",   label: "Utility Charges",   render: (row) => `${currencySymbol}${row.utilityCharges.toFixed(4)}`   },
    { id: "serviceCharges",   label: "Service Charges",   render: (row) => `${currencySymbol}${row.serviceCharges.toFixed(4)}`   },
    { id: "authenticationCharges",   label: "Authentication Charges",   render: (row) => `${currencySymbol}${row.authenticationCharges.toFixed(4)}`   },
    {
      id: "total",
      label: "Total Charges",
      render: (row) => <span style={{ fontWeight: 600 }}>{currencySymbol}{row.total.toFixed(4)}</span>,
    },
  ];

  const countryTableColumns = [
    { id: "country", label: "Country", render: (row) => <span style={{ fontWeight: 400 }}>{row.country}</span> },
    { id: "marketing_rate",    label: "Rate",    render: (row) => `${currencySymbol}${(row.marketing?.rate    ?? 0).toFixed(4)}` },
    { id: "marketing_count",   label: "Count",   render: (row) => row.marketing?.count   ?? 0 },
    { id: "marketing_charges", label: "Charges", render: (row) => `${currencySymbol}${(row.marketing?.charges ?? 0).toFixed(3)}` },
    { id: "utility_rate",      label: "Rate",    render: (row) => `${currencySymbol}${(row.utility?.rate      ?? 0).toFixed(4)}` },
    { id: "utility_count",     label: "Count",   render: (row) => row.utility?.count     ?? 0 },
    { id: "utility_charges",   label: "Charges", render: (row) => `${currencySymbol}${(row.utility?.charges   ?? 0).toFixed(3)}` },
    { id: "service_rate",      label: "Rate",    render: (row) => `${currencySymbol}${(row.service?.rate      ?? 0).toFixed(4)}` },
    { id: "service_count",     label: "Count",   render: (row) => row.service?.count     ?? 0 },
    { id: "service_charges",   label: "Charges", render: (row) => `${currencySymbol}${(row.service?.charges   ?? 0).toFixed(3)}` },
    { id: "auth_rate",         label: "Rate",    render: (row) => `${currencySymbol}${(row.auth?.rate         ?? 0).toFixed(4)}` },
    { id: "auth_count",        label: "Count",   render: (row) => row.auth?.count        ?? 0 },
    { id: "auth_charges",      label: "Charges", render: (row) => `${currencySymbol}${(row.auth?.charges      ?? 0).toFixed(3)}` },
  ];

  const topCountriesTableColumns = [
    { id: "country",   label: "Country",           render: (row) => <span>{row.country}</span> },
    { id: "marketing", label: "Marketing Messages" },
    { id: "utility",   label: "Utility Messages"   },
    { id: "service",   label: "Service Messages"   },
  ];

  const headCellStyle = {
    color: "white",
    fontWeight: 400,
    borderBottom: "none",
    whiteSpace: "nowrap",
    padding: "16px",
    backgroundColor: ColorsConstants.primaryBlue100,
  };

  const customHead = (
    <TableHead>
      <TableRow sx={{ backgroundColor: ColorsConstants.primaryBlue100 }}>
        <TableCell sx={headCellStyle} rowSpan={2}>Country</TableCell>
        <TableCell sx={{ ...headCellStyle, textAlign: "center" }} colSpan={3}>Marketing</TableCell>
        <TableCell sx={{ ...headCellStyle, textAlign: "center" }} colSpan={3}>Utility</TableCell>
        <TableCell sx={{ ...headCellStyle, textAlign: "center" }} colSpan={3}>Service</TableCell>
        <TableCell sx={{ ...headCellStyle, textAlign: "center" }} colSpan={3}>Authentication</TableCell>
      </TableRow>
      <TableRow sx={{ backgroundColor: ColorsConstants.primaryBlue100 }}>
        {["Rate","Count","Charges","Rate","Count","Charges","Rate","Count","Charges","Rate","Count","Charges"].map((label, idx) => (
          <TableCell key={idx} sx={headCellStyle}>{label}</TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  // ── Sub-components ─────────────────────────────────────────────────────────

  const StatsCard = ({ title, icon, bgColor, amount, noOfMessages }) => (
    <Card elevation={0} sx={{ backgroundColor: "white", borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "start", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="body2" sx={{ ...typography.bodyLarge, color: grayFontColor, mt: 0.5, mb: 2 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ color: ColorsConstants.primaryBlue100, fontWeight: 500, mb: 1 }}>
              {amount}
            </Typography>
            <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryPurple110 }}>
              {noOfMessages} Messages
            </Typography>
          </Box>
          <Box sx={{ backgroundColor: bgColor, width: 40, height: 40, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", mb: 1 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // ── Tabs config ────────────────────────────────────────────────────────────
  // 🟢 3. Mutually Exclusive Channel Tabs Layout
  const allChannelTabs = [
    { label: "WhatsApp", value: 0, show: hasWa },
    { label: "RCS", value: 1, show: hasRcsBotCreate }
  ];
  
  const channelTabs = allChannelTabs.filter(tab => tab.show !== false);

  const filterTabs  = [{ label: "Monthly Usage", value: 0 }, { label: "Charges Per Country", value: 1 }, { label: "Top Countries", value: 2 }];

  const cardsDataArray = [
    { amount: `${currencySymbol}${stats.total.charges.toFixed(2)}`,     title: "Total Amount", bgColor: "#1976D2", icon: <TotalAmountCardIcon />, noOfMessages: stats.total.messages?.toLocaleString()     },
    { amount: `${currencySymbol}${stats.marketing.charges.toFixed(2)}`, title: "Marketing",    bgColor: "#2E7D32", icon: <MarektingCardIcon />,   noOfMessages: stats.marketing.messages?.toLocaleString() },
    { amount: `${currencySymbol}${stats.utility.charges.toFixed(2)}`,   title: "Utility",      bgColor: "#9C27B0", icon: <UtilityCardIcon />,     noOfMessages: stats.utility.messages?.toLocaleString()   },
    { amount: `${currencySymbol}${stats.service.charges.toFixed(2)}`,   title: "Services",     bgColor: "#ED6C02", icon: <ServicesCardIcon />,    noOfMessages: stats.service.messages?.toLocaleString()   },
  ];

  const syncLabel = (color = ColorsConstants.secondaryBackgroundColor) => (
    <Box sx={{ color, px: 1.5, py: 0.5, borderRadius: 1, fontSize: "14px", fontWeight: 500 }}>
      Last synced on: {lastSync}
    </Box>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', py: 3, px: 3 }}>
      <Box>
        {/* ── UNIFIED PAGE HEADER (Always Visible) ── */}
        <Box sx={{ p: 3, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ width: "30%"}}>
              <Typography sx={{ ...typography.h1, mb: 0.5, fontWeight: 500 }}>Usage Charges Dashboard</Typography>
              <Typography sx={{ ...typography.h2, color: grayFontColor }}>Multi-channel messaging analytics and billing</Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Channel Tabs */}
              <CustomTabs
                tabs={channelTabs}
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                activeBgColor='#1976D2'
                inactiveBgColor='#e1e9f0'
                backgroundColor='#e1e9f0'
              />

              {/* Shared Date Filter */}
              <Box sx={{ display: "flex", alignItems: "center", width: 200, height: 53, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: "8px", backgroundColor: "white" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["year", "month"]}
                    openTo="month"
                    maxDate={dayjs()}
                    value={selectedMonth ? dayjs(selectedMonth) : null}
                    onChange={(newValue) => { if (newValue) setSelectedMonth(newValue.format("YYYY-MM")); }}
                    slots={{ openPickerIcon: CalendarIcon }}
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        size: "small",
                        sx: {
                          width: "100%",
                          "& .MuiOutlinedInput-root": { height: 53, borderRadius: "8px" },
                          "& .MuiInputBase-input": { fontSize: "14px", cursor: "pointer", py: 0 },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              {/* Shared Export Button */}
              <Button
                variant="contained"
                startIcon={<ExportIcon />}
                onClick={() => {
                  if (activeTab === 0) handleExport();
                  else if (activeTab === 1) handleRcsExport(); // 🟢 NEW: Calls RCS export
                }}
                sx={{
                  backgroundColor: ColorsConstants.secondaryBackgroundColor,
                  textTransform: 'none',
                  fontSize: "14px",
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: ColorsConstants.primaryPurple90, boxShadow: 'none' },
                  py: 2,
                  borderRadius: "8px",
                }}
              >
                Export
              </Button>
            </Box>
          </Box>
        </Box>

        {/* ════════════════════════════════════════════════════════════════════
            WHATSAPP USAGE VIEW
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 0 && (
          <Box sx={{ px: 3 }}>
            {/* Stats Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
              {cardsDataArray.map((card) => (
                <StatsCard key={card.title} amount={card.amount} bgColor={card.bgColor} icon={card.icon} noOfMessages={card.noOfMessages} title={card.title} />
              ))}
            </Box>

            {/* Filter Tabs */}
            <Box sx={{ mb: 2 }}>
              <CustomTabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} tabs={filterTabs} inactiveBgColor='#e1e9f0' backgroundColor='#e1e9f0' />
            </Box>

            <Paper elevation={0} sx={{ border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 3 }}>

                {/* Monthly Usage */}
                {currentTab === 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography sx={{ color: "#000000DE", ...typography.h1, fontWeight: 500 }}>Monthly Usage</Typography>
                      {syncLabel()}
                    </Box>
                    <AppTable columns={tableColumns} rows={monthlyUsage ?? []} rowKey={(row, index) => index} emptyMessage="No data available" stickyHeader={false} hideMainScroll={false} count={monthlyUsage?.length ?? 0} page={monthlyController.page} rowsPerPage={monthlyController.rowsPerPage} rowsPerPageOptions={[20, 50, 100]} onPageChange={handleMonthlyPageChange} onRowsPerPageChange={handleMonthlyRowsPerPageChange} />
                  </Box>
                )}

                {/* Charges Per Country */}
                {currentTab === 1 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h6" sx={{ color: "#000000DE", ...typography.h1, fontWeight: 500 }}>Charges Per Country</Typography>
                      {syncLabel()}
                    </Box>
                    <AppTable columns={countryTableColumns} rows={Array.isArray(chargesPerCountry) ? chargesPerCountry : []} rowKey={(row, index) => index} customHead={customHead} customHeadColSpan={13} emptyMessage="No data available"  paperSx={{ border: "none", boxShadow: "none", mb: 0 }} containerSx={{ overflowX: "auto" }} stickyHeader={false} hideMainScroll={false} count={rowCount} page={controller.page} rowsPerPage={controller.rowsPerPage} rowsPerPageOptions={[20, 50, 100]} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
                  </Box>
                )}

                {/* Top Countries */}
                {currentTab === 2 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h6" sx={{ color: "#000000DE", fontWeight: 500 }}>Top Countries</Typography>
                      {syncLabel()}
                    </Box>
                    <AppTable columns={topCountriesTableColumns} rows={topCountries} rowKey={(row, index) => index} emptyMessage="No data available" paperSx={{ border: "none", boxShadow: "none", mb: 0 }} stickyHeader={false} hideMainScroll={false} />
                  </Box>
                )}

              </Box>
            </Paper>
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            RCS USAGE VIEW
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 1 && (
          <Box sx={{ px: 3 }}>
            {/* 🟢 NEW: Pass onDataLoad to catch the API results */}
            <RCSUsageDashboard selectedMonth={selectedMonth} onDataLoad={setRcsUsageData} />
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default UsageCharges;